/**
 * A room's hours and blockouts (ZOY-636), shown wherever the room is
 * managed. Product 2026-09-14: a room with NO configured hours is open by
 * hours — capability, blockouts, busy and the bookable switch still apply —
 * so an empty week is said to be open, never rendered as seven "Closed"
 * rows. Configured hours close the room outside them. The routes are the
 * existing availability ones under clinic.availability.manage (not
 * rooms.manage); with availability.read only, the facts are listed and the
 * forms are absent.
 */

import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import a from '../access/access.module.css';
import { Button, Chip, Notice, Skeleton, TextField } from '@/ui/kit';
import { FieldGrid, FormCard, SelectField } from '@/ui/formkit';
import {
  addRoomBlockout,
  addRoomWorkingHours,
  deleteRoomBlockout,
  deleteRoomWorkingHours,
  fetchRoomAvailability,
} from '@/core/clinic/setupApi';
import { ApiError } from '@/core/api/errors';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { useLocale } from '@/core/i18n/LocaleProvider';
import { formatDate, formatTime, localizeDigits } from '@/core/calendar/format';
import { useClinicSettings } from '../patients/useClinicSettings';
import { DAY_IDS, hhmmToMinute, minuteToHHMM } from './availabilityPresets';
import { TimeOffForm, useDayNames } from './PractitionerAvailability';

export function RoomAvailabilityCard(props: { roomId: string; manage: boolean }) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const { locale } = useLocale();
  const authz = useAuthz();
  const settings = useClinicSettings();
  const tz = settings?.timezone ?? 'Asia/Tehran';
  const cal = settings?.calendarSystem ?? 'jalali';
  const queryClient = useQueryClient();
  const { dayName, dayNames } = useDayNames();
  const key = ['availability', 'room', authz.clinicId, props.roomId];
  const avail = useQuery({
    queryKey: key,
    queryFn: () => fetchRoomAvailability(props.roomId),
    retry: false,
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: key });
  const [notice, setNotice] = useState<string | null>(null);
  const removeRange = useMutation({
    mutationFn: (id: string) => deleteRoomWorkingHours(id),
    onSuccess: invalidate,
    onError: () => setNotice(t('hours.removeFailed')),
  });
  const removeBlockout = useMutation({
    mutationFn: (id: string) => deleteRoomBlockout(id),
    onSuccess: invalidate,
    onError: () => setNotice(t('hours.removeFailed')),
  });
  const hours = avail.data?.workingHours ?? [];
  const blockouts = avail.data?.blockouts ?? [];

  if (avail.isError)
    return (
      <Notice
        kind="err"
        role="alert"
        action={
          <Button variant="quiet" onClick={() => void avail.refetch()}>
            {t('table.retry')}
          </Button>
        }
      >
        {t('rec.sectionFailed')}
      </Notice>
    );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
        <div style={{ padding: '14px 18px 4px' }}>
          <strong style={{ color: 'var(--zc-teal-deep)' }}>{t('rooms.hoursTitle')}</strong>
          <p style={{ fontSize: 12.5, color: 'var(--zc-muted)', marginBlockStart: 4 }}>
            {t('rooms.hoursSub')}
          </p>
        </div>
        {avail.isPending && (
          <div className={a.row}>
            <Skeleton height="14px" width="50%" />
          </div>
        )}
        {/* An empty week is OPEN by hours — never seven "Closed" rows. */}
        {!avail.isPending && hours.length === 0 && (
          <div className={a.row}>
            <span className={a.rowValue}>
              <Chip kind="ok">{t('rooms.hoursOpen')}</Chip>
            </span>
          </div>
        )}
        {!avail.isPending &&
          hours.length > 0 &&
          DAY_IDS.map((d) => {
            const ranges = hours.filter((h) => h.dayOfWeek === d);
            return (
              <div key={d} className={a.row}>
                <span className={a.rowKey}>{dayName(d)}</span>
                <span className={a.rowValue} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {ranges.length === 0 && (
                    <span style={{ color: 'var(--zc-faint)' }}>{t('hours.closed')}</span>
                  )}
                  {ranges.map((r) => (
                    <span
                      key={r.id}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <Chip kind="ok">
                        <span className="zc-tabular">
                          {localizeDigits(
                            `${minuteToHHMM(r.startMinute)}–${minuteToHHMM(r.endMinute)}`,
                            locale,
                          )}
                        </span>
                      </Chip>
                      {props.manage && (
                        <Button variant="link" onClick={() => removeRange.mutate(r.id)}>
                          {t('common.remove')}
                        </Button>
                      )}
                    </span>
                  ))}
                </span>
              </div>
            );
          })}
        {notice && (
          <div className={a.row}>
            <Notice kind="err" role="alert">
              {notice}
            </Notice>
          </div>
        )}
      </div>

      {props.manage ? (
        <RoomRangeForm roomId={props.roomId} dayNames={dayNames} onAdded={invalidate} />
      ) : (
        <Notice kind="info">{t('rooms.hoursNeedManage')}</Notice>
      )}

      <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
        <div style={{ padding: '14px 18px 4px' }}>
          <strong style={{ color: 'var(--zc-teal-deep)' }}>{t('rooms.blockoutsTitle')}</strong>
          <p style={{ fontSize: 12.5, color: 'var(--zc-muted)', marginBlockStart: 4 }}>
            {t('rooms.blockoutsSub')}
          </p>
        </div>
        {!avail.isPending && blockouts.length === 0 && (
          <div className={a.row}>
            <span className={a.rowValue} style={{ color: 'var(--zc-faint)' }}>
              {t('rooms.blockoutsNone')}
            </span>
          </div>
        )}
        {blockouts.map((o) => (
          <div key={o.id} className={a.row}>
            <span className={a.rowValue}>
              {formatDate(o.startsAt, { locale, calendar: cal, timezone: tz })}{' '}
              <span className="zc-tabular">{formatTime(o.startsAt, { locale, timezone: tz })}</span>
              {' — '}
              {formatDate(o.endsAt, { locale, calendar: cal, timezone: tz })}{' '}
              <span className="zc-tabular">{formatTime(o.endsAt, { locale, timezone: tz })}</span>
              {o.reason ? ` · ${o.reason}` : ''}
            </span>
            {props.manage && (
              <span className={a.rowAction}>
                <Button variant="link" onClick={() => removeBlockout.mutate(o.id)}>
                  {t('common.remove')}
                </Button>
              </span>
            )}
          </div>
        ))}
      </div>

      {props.manage && (
        <TimeOffForm
          uid={props.roomId}
          add={(range) => addRoomBlockout(props.roomId, range)}
          title={t('rooms.recordBlockout')}
          cta={t('rooms.blockoutCta')}
          note={t('rooms.blockoutNote')}
          onAdded={invalidate}
        />
      )}
    </div>
  );
}

/** Add one weekly range to the room. The contract is one range per write; a 409 is an overlap. */
function RoomRangeForm(props: { roomId: string; dayNames: string[]; onAdded: () => void }) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [day, setDay] = useState('6');
  const [from, setFrom] = useState('09:00');
  const [to, setTo] = useState('17:00');
  const [error, setError] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: (range: { dayOfWeek: number; startMinute: number; endMinute: number }) =>
      addRoomWorkingHours(props.roomId, range),
    onSuccess: () => {
      setError(null);
      props.onAdded();
    },
    onError: (err) =>
      setError(
        err instanceof ApiError && err.status === 409
          ? t('hours.overlap')
          : err instanceof ApiError && err.serverMessage
            ? err.serverMessage
            : t('common.failedKept'),
      ),
  });
  const submit = () => {
    const s = hhmmToMinute(from);
    const e = hhmmToMinute(to);
    if (s === null || e === null || e <= s) {
      setError(t('hours.timeFormat'));
      return;
    }
    setError(null);
    add.mutate({ dayOfWeek: Number(day), startMinute: s, endMinute: e });
  };
  return (
    <FormCard
      title={t('hours.addRange')}
      onSubmit={submit}
      cta={t('common.add')}
      busy={add.isPending}
    >
      <FieldGrid>
        <SelectField
          label={t('hours.day')}
          value={day}
          onChange={setDay}
          options={DAY_IDS.map((d, i) => ({ value: String(d), label: props.dayNames[i] ?? '' }))}
        />
        <TextField
          label={t('hours.from')}
          value={from}
          onChange={setFrom}
          help={t('hours.timeFormat')}
          machine
          error={error ?? undefined}
        />
        <TextField
          label={t('hours.to')}
          value={to}
          onChange={setTo}
          help={t('hours.timeFormat')}
          machine
        />
      </FieldGrid>
    </FormCard>
  );
}
